// FitAnalyzerDialog.js

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  List,
  ListItem,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarIcon from "@mui/icons-material/Star";
import { toast } from "react-toastify";

const customColors = {
  success: { main: "#2e7d32", lightest: "#edf7ed" },
  error: { main: "#d32f2f", lightest: "#fdeded" },
  info: { main: "#0288d1", lightest: "#e5f6fd" },
  secondary: { main: "#9c27b0", lightest: "#f3e5f5" },
};

const getScoreColor = (score) => {
  if (score <= 10) return "inherit";
  if (score < 50) return "error";
  if (score < 75) return "warning";
  return "success";
};

const getScoreLabel = (score) => {
  if (score <= 10) return "Mismatch";
  if (score < 50) return "Low Fit";
  if (score < 75) return "Good Fit";
  return "Strong Fit";
};

const getChipColor = (criticality) => {
  switch (criticality?.toLowerCase()) {
    case "high":
      return "error";
    case "medium":
      return "warning";
    case "low":
      return "info";
    default:
      return "default";
  }
};

const jdPlaceholder = `e.g.,
Job Title: Senior Frontend Developer

Responsibilities:
- Develop and maintain responsive user interfaces.
- Collaborate with product managers and backend teams.

Requirements:
- 5+ years of experience with React & TypeScript.
- Strong skills in HTML, CSS, and modern JavaScript.`;

const StrengthItem = ({ item }) => (
  <ListItem
    sx={{
      display: "block",
      bgcolor: customColors.success.lightest,
      borderLeft: 3,
      borderColor: customColors.success.main,
      mb: 1,
      p: 1.5,
      borderRadius: 1,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
      <Chip
        label={item.criticality}
        color={getChipColor(item.criticality)}
        size="small"
        sx={{ mr: 1 }}
      />
      <Typography variant="body1" fontWeight="bold">
        {item.jd_requirement}
      </Typography>
    </Box>
    <Typography variant="body2" color="text.secondary">
      <em>Evidence: "{item.resume_evidence}"</em>
    </Typography>
  </ListItem>
);

const GapItem = ({ item }) => (
  <ListItem
    sx={{
      display: "block",
      bgcolor: customColors.error.lightest,
      borderLeft: 3,
      borderColor: customColors.error.main,
      mb: 1,
      p: 1.5,
      borderRadius: 1,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
      <Chip
        label={item.criticality}
        color={getChipColor(item.criticality)}
        size="small"
        sx={{ mr: 1 }}
      />
      <Typography variant="body1" fontWeight="bold">
        {item.jd_requirement}
      </Typography>
    </Box>
    <Typography variant="body2" color="text.secondary">
      <em>Suggestion: "{item.suggested_actions}"</em>
    </Typography>
  </ListItem>
);

const PotentialItem = ({ item, type }) => (
  <ListItem
    sx={{
      display: "block",
      bgcolor: customColors.info.lightest,
      borderLeft: 3,
      borderColor: customColors.info.main,
      mb: 1,
      p: 1.5,
      borderRadius: 1,
    }}
  >
    <Typography variant="body1" fontWeight="bold">
      [{type}] {item.jd_requirement}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      <strong>Candidate Has:</strong> {item.resume_evidence}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      <strong>
        {type === "Partial Match" ? "Analysis:" : "Justification:"}
      </strong>{" "}
      {item.analysis || item.justification}
    </Typography>
  </ListItem>
);

const DifferentiatorItem = ({ item }) => (
  <ListItem
    sx={{
      display: "flex",
      alignItems: "flex-start",
      bgcolor: customColors.secondary.lightest,
      borderLeft: 3,
      borderColor: customColors.secondary.main,
      mb: 1,
      p: 1.5,
      borderRadius: 1,
    }}
  >
    <StarIcon
      fontSize="small"
      sx={{ color: customColors.secondary.main, mr: 1, mt: 0.5 }}
    />
    <Typography variant="body1" fontStyle="italic">
      {item}
    </Typography>
  </ListItem>
);

const FitAnalyzerDialog = ({ open, onClose, cvBase64DataUrl, category }) => {
  const docLabel = category === "Enterprise" ? "Pitch Deck" : "CV";
  const [jobDescription, setJobDescription] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [isJdValid, setIsJdValid] = useState(false);

  const MIN_JD_WORDS = 10;

  const countWords = (str) => {
    if (!str) return 0;
    return str.trim().split(/\s+/).filter(Boolean).length;
  };

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setJobDescription("");
        setAnalysisResult(null);
        setError(null);
        setIsAnalyzing(false);
        setIsRateLimited(false);
      }, 300);
    }
  }, [open]);

  const handleAnalyzeClick = async () => {
    const jdWordCount = countWords(jobDescription);
    if (jdWordCount < MIN_JD_WORDS) {
      toast.error(
        `The job description must contain at least ${MIN_JD_WORDS} words.`
      );
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalysisResult(null);

    try {
      const response = await fetch("/api/analyzer/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cvBase64DataUrl, jobDescription }),
      });

      if (response.status === 429) {
        const errorData = await response.json();
        setError(errorData.error);
        setIsRateLimited(true);

        const waitMatch = errorData.error.match(/\d+/);
        if (waitMatch) {
          const waitSeconds = parseInt(waitMatch[0], 10);
          setTimeout(() => {
            setIsRateLimited(false);
            setError(null);
          }, waitSeconds * 1000);
        }
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Server error ${response.status}`);
      }

      setAnalysisResult(data);
    } catch (err) {
      console.error("Analysis failed:", err);
      setError(err.message);
      toast.error(` ${err.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const renderContent = () => {
    if (analysisResult) {
      const {
        match_score = 0,
        one_line_summary = "No summary provided.",
        strategic_analysis = {},
      } = analysisResult;
      const scoreColor = getScoreColor(match_score);
      const scoreLabel = getScoreLabel(match_score);

      const {
        strengths = [],
        gaps = [],
        potential,
        differentiators = [],
      } = strategic_analysis || {};
      const { partial_matches = [], transferable_skills = [] } =
        potential || {};

      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
            gap: 4,
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              pr: { md: 4 },
              borderRight: { md: "1px solid" },
              borderColor: { md: "divider" },
            }}
          >
            <Box sx={{ position: "relative", display: "inline-flex", mb: 2 }}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={120}
                sx={{ color: (theme) => theme.palette.grey[200] }}
              />
              <CircularProgress
                thickness={5}
                variant="determinate"
                value={match_score}
                size={120}
                color={scoreColor}
                sx={{
                  position: "absolute",
                  left: 0,
                  "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h4" component="div" fontWeight="bold">
                  {`${match_score}%`}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {scoreLabel}
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" fontStyle="italic" color="text.secondary">
              {one_line_summary.replace(/^"|"$/g, "")}
            </Typography>
          </Box>

          <Box>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Strengths</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List disablePadding>
                  {strengths.length > 0 ? (
                    strengths.map((item, index) => (
                      <StrengthItem key={`str-${index}`} item={item} />
                    ))
                  ) : (
                    <Typography variant="body2">
                      No direct strengths identified.
                    </Typography>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">
                  Gaps & Recommendations
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List disablePadding>
                  {gaps.length > 0 ? (
                    gaps.map((item, index) => (
                      <GapItem key={`gap-${index}`} item={item} />
                    ))
                  ) : (
                    <Typography variant="body2">
                      No significant gaps identified.
                    </Typography>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">
                  Potential & Transferable Skills
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List disablePadding>
                  {partial_matches.length > 0 &&
                    partial_matches.map((item, index) => (
                      <PotentialItem
                        key={`pm-${index}`}
                        item={item}
                        type="Partial Match"
                      />
                    ))}
                  {transferable_skills.length > 0 &&
                    transferable_skills.map((item, index) => (
                      <PotentialItem
                        key={`ts-${index}`}
                        item={item}
                        type="Transferable Skill"
                      />
                    ))}
                  {partial_matches.length === 0 &&
                    transferable_skills.length === 0 && (
                      <Typography variant="body2">
                        No notable potential or transferable skills identified.
                      </Typography>
                    )}
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">
                  Candidate Differentiators (X-Factor)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List disablePadding>
                  {differentiators.length > 0 ? (
                    differentiators.map((item, index) => (
                      <DifferentiatorItem key={`diff-${index}`} item={item} />
                    ))
                  ) : (
                    <Typography variant="body2">
                      No unique differentiating factors were found.
                    </Typography>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography>
          Please provide the job description. The AI will assess the {docLabel} against
          it and generate a detailed fit-gap analysis.
        </Typography>

        <TextField
          autoFocus
          multiline
          fullWidth
          rows={10}
          placeholder={jdPlaceholder}
          value={jobDescription}
          onChange={(e) => {
            const text = e.target.value;
            setJobDescription(text);
            setIsJdValid(countWords(text) >= MIN_JD_WORDS);
          }}
          helperText={`${countWords(jobDescription)} / ${MIN_JD_WORDS} words`}
          FormHelperTextProps={{
            sx: {
              textAlign: "right",
              color: isJdValid ? "success.main" : "text.secondary",
              fontWeight: isJdValid ? "bold" : "normal",
            },
          }}
        />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontStyle: "italic",
            display: "block",
            mt: 1,
          }}
        >
          Note: The {docLabel} and the job description will be analyzed by AI to
          generate insights and recommendations.
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        AI Analysis Report
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {renderContent()}
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        {!analysisResult ? (
          <LoadingButton
            variant="contained"
            onClick={handleAnalyzeClick}
            loading={isAnalyzing}
            disabled={!isJdValid || isRateLimited}
          >
            Analyze Candidate
          </LoadingButton>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FitAnalyzerDialog;
